import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCents } from "@/lib/format";
import type { FolhaItem } from "@/data/folha";
import { AlertTriangle } from "lucide-react";

interface Props {
  itens: FolhaItem[];
  editavel: boolean;
  onAjustar: (item: FolhaItem) => void;
  onDetalhar: (item: FolhaItem) => void;
}

const Valor = ({ cents, desconto }: { cents: number; desconto?: boolean }) => (
  <span
    className={`tabular-nums ${cents === 0 ? "text-muted-foreground" : desconto ? "text-destructive" : ""}`}
  >
    {cents === 0 ? "—" : desconto ? `− ${formatCents(cents)}` : formatCents(cents)}
  </span>
);

export const FolhaItensTable = ({ itens, editavel, onAjustar, onDetalhar }: Props) => (
  <div className="overflow-hidden rounded-xl border bg-card">
    <Table className="min-w-[1000px]">
        <TableHeader>
          <TableRow>
            <TableHead className="pl-5">Colaborador</TableHead>
            <TableHead className="text-right">Fixo</TableHead>
            <TableHead className="text-right">Comissões</TableHead>
            <TableHead className="text-right">Amil</TableHead>
            <TableHead className="text-right">Empréstimos/descontos</TableHead>
            <TableHead className="text-right">Ajuste</TableHead>
            <TableHead className="text-right">Líquido</TableHead>
            <TableHead className="w-24"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {itens.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="py-12 text-center text-muted-foreground">
                Nenhum colaborador nesta competência. Cadastre o salário fixo ou importe as
                planilhas de comissão e Amil.
              </TableCell>
            </TableRow>
          ) : (
            itens.map((i) => {
              const problema = i.liquido <= 0 || !i.tomadorId;
              return (
                <TableRow key={i.id} className={problema ? "bg-destructive/[.04]" : ""}>
                  <TableCell className="pl-5">
                    <button
                      type="button"
                      onClick={() => onDetalhar(i)}
                      className="text-left font-medium hover:underline"
                    >
                      {i.nome}
                    </button>
                    <div className="text-xs text-muted-foreground">{i.email}</div>
                    {!i.tomadorId && (
                      <Badge variant="outline" className="mt-1 text-[10px] text-destructive">
                        Sem tomador
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Valor cents={i.salarioFixo} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Valor cents={i.comissoes} />
                  </TableCell>
                  <TableCell className="text-right">
                    {i.amilRepasseTotal === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help tabular-nums text-destructive underline decoration-dotted underline-offset-2">
                            − {formatCents(i.amilRepasseTotal)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>
                            Titular: {formatCents(i.amilTitularBruto)} em despesas, franquia de{" "}
                            {formatCents(i.amilFranquiaAplicada)} coberta pela empresa → repasse de{" "}
                            {formatCents(i.amilTitularRepasse)}
                          </p>
                          <p className="mt-1">
                            Dependentes: {formatCents(i.amilDependente)} (repasse integral)
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Valor cents={i.emprestimoParcela} desconto />
                  </TableCell>
                  <TableCell className="text-right">
                    {i.ajusteManual === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help tabular-nums underline decoration-dotted underline-offset-2">
                            {formatCents(i.ajusteManual)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{i.ajusteMotivo ?? "Sem motivo informado"}</TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    <span
                      className={`inline-flex items-center gap-1 tabular-nums ${
                        i.liquido <= 0 ? "text-destructive" : ""
                      }`}
                    >
                      {i.liquido <= 0 && <AlertTriangle className="h-3.5 w-3.5" />}
                      {formatCents(i.liquido)}
                    </span>
                  </TableCell>
                  <TableCell className="pr-4 text-right">
                    {editavel && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-[13px] text-primary-text hover:text-primary-text"
                        onClick={() => onAjustar(i)}
                        aria-label={`Ajustar linha de ${i.nome}`}
                      >
                        Ajustar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
    </Table>
  </div>
);
